import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import DefaultLayout from '../../components/layouts/DefaultLayout'
import AdminNav from '../../components/layouts/AdminNav'
import {
  Box,
  Container,
  Heading,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  HStack,
  Button,
  Select,
  Card,
  CardBody,
  useBreakpointValue,
  IconButton,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Image,
  SimpleGrid,
  Checkbox,
} from '@chakra-ui/react'
import { FiTrash2, FiEye, FiEdit, FiPlus, FiX } from 'react-icons/fi'
import {
  getAdminNoticeList,
  deleteAdminNotice,
  createNotice,
  updateNotice,
} from '../../services/apiService'
import dayjs from 'dayjs'

const adminNavItems = [
  { label: '회원 관리', path: '/admin/user' },
  { label: '커뮤니티 관리', path: '/admin/community' },
  { label: 'Q&A 관리', path: '/admin/qna' },
  { label: '공지사항 관리', path: '/admin/notice' },
  { label: '자산 관리', path: '/admin/asset' },
  { label: '경매사례 관리', path: '/admin/auction-case' },
]

interface Notice {
  id: number
  userId: number
  username: string
  title: string
  content: string
  images?: string[] | null
  isPopup?: number
  viewCount: number
  createdAt: string
  updatedAt: string | null
}

const Notice = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const [notices, setNotices] = useState<Notice[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const isMobile = useBreakpointValue({ base: true, md: false })
  const { isOpen, onOpen, onClose } = useDisclosure()
  const deleteDialog = useDisclosure()
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const cancelRef = React.useRef<HTMLButtonElement>(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const [editingNotice, setEditingNotice] = useState<Notice | null>(null)
  const [formTitle, setFormTitle] = useState('')
  const [formContent, setFormContent] = useState('')
  const [formImages, setFormImages] = useState<File[]>([])
  const [existingImages, setExistingImages] = useState<string[]>([])
  const [imagePreviews, setImagePreviews] = useState<string[]>([])
  const [isPopup, setIsPopup] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const fetchNotices = async () => {
    setLoading(true)
    try {
      const { data: res } = await getAdminNoticeList(page, limit)
      if (res.statusCode === 200) {
        setNotices(res.data.notices)
        setTotal(res.data.total)
        setTotalPages(res.data.totalPages)
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('공지사항 목록을 불러오는데 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchNotices()
  }, [page, limit])

  const handleCreate = () => {
    setIsEditMode(false)
    setEditingNotice(null)
    setFormTitle('')
    setFormContent('')
    setFormImages([])
    setExistingImages([])
    setImagePreviews([])
    setIsPopup(false)
    onOpen()
  }

  const handleEdit = (notice: Notice) => {
    setIsEditMode(true)
    setEditingNotice(notice)
    setFormTitle(notice.title)
    setFormContent(notice.content)
    setFormImages([])
    setExistingImages(notice.images || [])
    setImagePreviews(notice.images || [])
    setIsPopup(notice.isPopup === 1)
    onOpen()
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    const totalImages = existingImages.length + formImages.length + files.length
    
    if (totalImages > 3) {
      toast({
        title: t('이미지 업로드 오류'),
        description: t('이미지는 최대 3장까지 업로드 가능합니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    const newFiles = files.slice(0, 3 - existingImages.length - formImages.length)
    setFormImages([...formImages, ...newFiles])

    // 미리보기 생성
    newFiles.forEach((file) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreviews((prev) => [...prev, reader.result as string])
      }
      reader.readAsDataURL(file)
    })
  }

  const handleRemoveImage = (index: number) => {
    // 기존 이미지인지 새 이미지인지 확인
    if (index < existingImages.length) {
      setExistingImages(existingImages.filter((_, i) => i !== index))
      setImagePreviews(imagePreviews.filter((_, i) => i !== index))
    } else {
      const newIndex = index - existingImages.length
      setFormImages(formImages.filter((_, i) => i !== newIndex))
      setImagePreviews(imagePreviews.filter((_, i) => i !== index))
    }
  }

  const handleSubmit = async () => {
    if (!formTitle.trim() || !formContent.trim()) {
      toast({
        title: t('입력 오류'),
        description: t('제목과 내용을 모두 입력해주세요.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    if (existingImages.length + formImages.length > 3) {
      toast({
        title: t('이미지 업로드 오류'),
        description: t('이미지는 최대 3장까지 업로드 가능합니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    setSubmitting(true)
    try {
      if (isEditMode && editingNotice) {
        const { data: res } = await updateNotice(
          editingNotice.id,
          formTitle,
          formContent,
          existingImages.length > 0 ? existingImages : undefined,
          formImages.length > 0 ? formImages : undefined,
          isPopup
        )
        if (res.statusCode === 200) {
          toast({
            title: t('성공'),
            description: res.message,
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
          onClose()
          fetchNotices()
        }
      } else {
        const { data: res } = await createNotice(
          formTitle,
          formContent,
          formImages.length > 0 ? formImages : undefined,
          isPopup
        )
        if (res.statusCode === 200) {
          toast({
            title: t('성공'),
            description: res.message,
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
          onClose()
          fetchNotices()
        }
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('저장에 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteClick = (id: number) => {
    setDeletingId(id)
    deleteDialog.onOpen()
  }

  const handleDeleteConfirm = async () => {
    if (!deletingId) return

    try {
      const { data: res } = await deleteAdminNotice(deletingId)
      if (res.statusCode === 200) {
        toast({
          title: t('성공'),
          description: res.message,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        deleteDialog.onClose()
        setDeletingId(null)
        fetchNotices()
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('삭제에 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  return (
    <DefaultLayout>
      <AdminNav items={adminNavItems} />
      <Container maxW="container.xl" py={10}>
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between">
            <Heading size="lg">공지사항 관리</Heading>
            <Button
              leftIcon={<FiPlus />}
              colorScheme="brand"
              onClick={handleCreate}
            >
              {t('작성')}
            </Button>
          </HStack>

          <Box
            p={6}
            bg="white"
            borderRadius="lg"
            boxShadow="sm"
          >
            {loading ? (
              <Box textAlign="center" py={10}>
                <Spinner size="xl" />
              </Box>
            ) : (
              <VStack spacing={4} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600">
                    총 {total}개의 공지사항
                  </Text>
                  <Select
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value))
                      setPage(1)
                    }}
                    width="120px"
                    size="sm"
                  >
                    <option value={10}>10개씩</option>
                    <option value={20}>20개씩</option>
                    <option value={50}>50개씩</option>
                  </Select>
                </HStack>

                {isMobile ? (
                  <VStack spacing={4} align="stretch">
                    {notices.length === 0 ? (
                      <Box textAlign="center" py={8}>
                        <Text color="gray.500">등록된 공지사항이 없습니다.</Text>
                      </Box>
                    ) : (
                      notices.map((notice) => (
                        <Card key={notice.id} size="sm">
                          <CardBody>
                            <VStack align="stretch" spacing={3}>
                              <HStack justify="space-between">
                                <Text fontWeight="bold" fontSize="md" noOfLines={1}>
                                  {notice.title}
                                </Text>
                                <HStack spacing={2}>
                                  <IconButton
                                    aria-label={t('상세보기')}
                                    icon={<FiEye />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => navigate(`/notice/${notice.id}`)}
                                  />
                                  <IconButton
                                    aria-label={t('수정')}
                                    icon={<FiEdit />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleEdit(notice)}
                                  />
                                  <IconButton
                                    aria-label={t('삭제')}
                                    icon={<FiTrash2 />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="red"
                                    onClick={() => handleDeleteClick(notice.id)}
                                  />
                                </HStack>
                              </HStack>
                              <Text fontSize="sm" color="gray.600" noOfLines={2}>
                                {notice.content}
                              </Text>
                              <HStack justify="space-between" fontSize="xs" color="gray.500">
                                <Text>{notice.username}</Text>
                                <HStack spacing={2}>
                                  <Text>조회 {notice.viewCount}</Text>
                                  <Text>{dayjs(notice.createdAt).format('YYYY-MM-DD')}</Text>
                                </HStack>
                              </HStack>
                            </VStack>
                          </CardBody>
                        </Card>
                      ))
                    )}
                  </VStack>
                ) : (
                  <Box overflowX="auto">
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>ID</Th>
                          <Th>제목</Th>
                          <Th>작성자</Th>
                          <Th>조회수</Th>
                          <Th>작성일시</Th>
                          <Th>관리</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {notices.length === 0 ? (
                          <Tr>
                            <Td colSpan={6} textAlign="center" py={8}>
                              <Text color="gray.500">등록된 공지사항이 없습니다.</Text>
                            </Td>
                          </Tr>
                        ) : (
                          notices.map((notice) => (
                            <Tr key={notice.id}>
                              <Td>{notice.id}</Td>
                              <Td>
                                <Text
                                  fontWeight="medium"
                                  cursor="pointer"
                                  _hover={{ color: 'brand.600' }}
                                  onClick={() => navigate(`/notice/${notice.id}`)}
                                  noOfLines={1}
                                  maxW="300px"
                                >
                                  {notice.title}
                                </Text>
                              </Td>
                              <Td>{notice.username}</Td>
                              <Td>{notice.viewCount}</Td>
                              <Td>
                                {dayjs(notice.createdAt).format('YYYY-MM-DD HH:mm')}
                              </Td>
                              <Td>
                                <HStack spacing={2}>
                                  <IconButton
                                    aria-label={t('상세보기')}
                                    icon={<FiEye />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => navigate(`/notice/${notice.id}`)}
                                  />
                                  <IconButton
                                    aria-label={t('수정')}
                                    icon={<FiEdit />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleEdit(notice)}
                                  />
                                  <IconButton
                                    aria-label={t('삭제')}
                                    icon={<FiTrash2 />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="red"
                                    onClick={() => handleDeleteClick(notice.id)}
                                  />
                                </HStack>
                              </Td>
                            </Tr>
                          ))
                        )}
                      </Tbody>
                    </Table>
                  </Box>
                )}

                {totalPages > 0 && (
                  <HStack justify="center" spacing={2}>
                    <Button
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      isDisabled={page === 1}
                    >
                      {t('이전')}
                    </Button>
                    <Text fontSize="sm">
                      {page} / {totalPages}
                    </Text>
                    <Button
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      isDisabled={page >= totalPages}
                    >
                      {t('다음')}
                    </Button>
                  </HStack>
                )}
              </VStack>
            )}
          </Box>
        </VStack>
      </Container>

      {/* 작성/수정 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isEditMode ? t('공지사항 수정') : t('공지사항 작성')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>{t('제목')}</FormLabel>
                <Input
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder={t('제목을 입력하세요')}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t('내용')}</FormLabel>
                <Textarea
                  value={formContent}
                  onChange={(e) => setFormContent(e.target.value)}
                  placeholder={t('내용을 입력하세요')}
                  rows={10}
                />
              </FormControl>
              <FormControl>
                <FormLabel>{t('이미지')} (최대 3장)</FormLabel>
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  isDisabled={existingImages.length + formImages.length >= 3}
                />
                <Text fontSize="sm" color="gray.500" mt={2}>
                  {existingImages.length + formImages.length} / 3
                </Text>
                {imagePreviews.length > 0 && (
                  <SimpleGrid columns={3} spacing={4} mt={4}>
                    {imagePreviews.map((preview, index) => (
                      <Box key={index} position="relative">
                        <Image
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          borderRadius="md"
                          objectFit="cover"
                          w="100%"
                          h="150px"
                        />
                        <IconButton
                          aria-label="이미지 삭제"
                          icon={<FiX />}
                          size="sm"
                          colorScheme="red"
                          position="absolute"
                          top={2}
                          right={2}
                          onClick={() => handleRemoveImage(index)}
                        />
                      </Box>
                    ))}
                  </SimpleGrid>
                )}
              </FormControl>
              <FormControl>
                <Checkbox
                  isChecked={isPopup}
                  onChange={(e) => setIsPopup(e.target.checked)}
                  colorScheme="brand"
                >
                  {t('팝업 공지로 설정')}
                </Checkbox>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  팝업 공지로 설정하면 홈 화면에 팝업으로 표시됩니다.
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              {t('취소')}
            </Button>
            <Button
              colorScheme="brand"
              onClick={handleSubmit}
              isLoading={submitting}
            >
              {isEditMode ? t('수정') : t('작성')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 삭제 확인 다이얼로그 */}
      <AlertDialog
        isOpen={deleteDialog.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={deleteDialog.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('삭제 확인')}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t('정말 삭제하시겠습니까?')}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={deleteDialog.onClose}>
                {t('취소')}
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                {t('삭제')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </DefaultLayout>
  )
}

export default Notice

